import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import { incrementVersion } from "@brewbase/lib";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Search,
  PlusIcon,
  LockIcon,
  CopyIcon,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { formatDate } from "@/lib/date";
import { z } from "zod";
import { useZodForm } from "@/components/form";
import type { MediumRecipe, MediumRecipeId } from "@brewbase/db/types";

export const Route = createFileRoute("/fermentations/medium-recipes/")({
  component: RouteComponent,
});

const newRecipeSchema = z.object({
  name: z.string().min(1, "Name is required"),
  version: z.string().min(1, "Version is required"),
  description: z.string().optional(),
  referenceWeightKg: z.string().optional(),
});

function RouteComponent() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRecipes, setExpandedRecipes] = useState<Set<string>>(
    new Set(),
  );
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);

  const { data: recipes, isLoading } = trpc.mediumRecipes.list.useQuery();

  const createMutation = trpc.mediumRecipes.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Recipe "${data.name}" created!`);
      utils.mediumRecipes.list.invalidate();
      setIsNewDialogOpen(false);
      navigate({
        to: "/fermentations/medium-recipes/$recipe_id",
        params: { recipe_id: data.id },
      });
    },
    onError: (error) => {
      toast.error(`Failed to create recipe: ${error.message}`);
    },
  });

  const createNewVersionMutation = trpc.mediumRecipes.createVersion.useMutation(
    {
      onSuccess: (data) => {
        toast.success(`New version "${data.version}" created!`);
        utils.mediumRecipes.list.invalidate();
        navigate({
          to: "/fermentations/medium-recipes/$recipe_id",
          params: { recipe_id: data.id },
        });
      },
      onError: (error) => {
        toast.error(`Failed to create new version: ${error.message}`);
      },
    },
  );

  const form = useZodForm({
    schema: newRecipeSchema,
    defaultValues: {
      name: "",
      version: "1.0",
      description: "",
      referenceWeightKg: "15",
    },
  });

  const toggleExpanded = (recipeName: string) => {
    setExpandedRecipes((prev) => {
      const next = new Set(prev);
      if (next.has(recipeName)) {
        next.delete(recipeName);
      } else {
        next.add(recipeName);
      }
      return next;
    });
  };

  // Group recipes by name (for versioning)
  const groupedRecipes: Record<string, MediumRecipe[]> = useMemo(() => {
    if (!recipes) return {};

    const filtered = recipes.filter((r) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        r.name.toLowerCase().includes(searchLower) ||
        r.version.toLowerCase().includes(searchLower) ||
        (r.description || "").toLowerCase().includes(searchLower)
      );
    });

    return filtered.reduce(
      (acc, recipe) => {
        const key = recipe.name;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(recipe);
        return acc;
      },
      {} as Record<string, MediumRecipe[]>,
    );
  }, [recipes, searchQuery]);

  const handleCreateRecipe = form.handleSubmit((values) => {
    createMutation.mutate({
      name: values.name,
      version: values.version,
      description: values.description || null,
      referenceWeightKg: values.referenceWeightKg || null,
    });
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">Loading recipes...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search recipes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Dialog open={isNewDialogOpen} onOpenChange={setIsNewDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <PlusIcon className="w-4 h-4 mr-1" />
              New Recipe
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Medium Recipe</DialogTitle>
              <DialogDescription>
                Create a new medium recipe with configurable components.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateRecipe}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Standard Medium"
                      {...form.register("name")}
                    />
                    {form.formState.errors.name && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="version">Version</Label>
                    <Input
                      id="version"
                      placeholder="e.g., 1.0"
                      {...form.register("version")}
                    />
                    {form.formState.errors.version && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.version.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="referenceWeightKg">
                    Reference Weight (kg, optional)
                  </Label>
                  <NumericInput
                    precise
                    id="referenceWeightKg"
                    placeholder="e.g., 15"
                    value={form.watch("referenceWeightKg") || null}
                    onValueChange={(v) =>
                      form.setValue("referenceWeightKg", v ?? "")
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Used for scaling concentration-based components in the
                    preview.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Input
                    id="description"
                    placeholder="Brief description..."
                    {...form.register("description")}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsNewDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Creating..." : "Create Recipe"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Recipe list */}
      <div className="divide-y divide-border rounded-md border">
        {Object.entries(groupedRecipes)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([name, versions]) => (
            <RecipeGroup
              key={name}
              name={name}
              versions={versions}
              searchQuery={searchQuery}
              expanded={expandedRecipes.has(name)}
              onToggle={() => toggleExpanded(name)}
              onCreateVersion={(id, version) =>
                createNewVersionMutation.mutate({
                  sourceRecipeId: id,
                  newVersion: incrementVersion(version),
                })
              }
              isCreatingVersion={createNewVersionMutation.isPending}
            />
          ))}

        {/* Empty state */}
        {Object.keys(groupedRecipes).length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-3 text-sm">
              {searchQuery
                ? "No recipes found matching your search."
                : "No medium recipes yet. Create your first one!"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ======================== Recipe Group ========================

interface RecipeGroupProps {
  name: string;
  versions: MediumRecipe[];
  searchQuery: string;
  expanded: boolean;
  onToggle: () => void;
  onCreateVersion: (id: MediumRecipeId, currentVersion: string) => void;
  isCreatingVersion: boolean;
}

function RecipeGroup({
  name,
  versions,
  searchQuery,
  expanded,
  onToggle,
  onCreateVersion,
  isCreatingVersion,
}: RecipeGroupProps) {
  const hasMultipleVersions = versions.length > 1;
  const latestVersion = versions[0];

  const versionsToShow =
    searchQuery || !hasMultipleVersions
      ? versions
      : expanded
        ? versions
        : [latestVersion];

  return (
    <div>
      {/* Recipe group header (if multiple versions) */}
      {hasMultipleVersions && !searchQuery && (
        <button
          onClick={onToggle}
          className="flex items-center justify-between w-full px-4 py-3 hover:bg-muted/50 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="font-medium">{name}</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {versions.length} versions
          </span>
        </button>
      )}

      {/* Version rows */}
      <div className={hasMultipleVersions && !searchQuery ? "pl-6" : ""}>
        {versionsToShow.map((recipe) => (
          <div
            key={recipe.id}
            className="group flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Link
                  to="/fermentations/medium-recipes/$recipe_id"
                  params={{ recipe_id: recipe.id }}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {hasMultipleVersions || searchQuery
                    ? `v${recipe.version}`
                    : recipe.name}
                </Link>

                {recipe.is_locked && (
                  <Badge
                    variant="outline"
                    className="gap-1 text-warning border-warning"
                  >
                    <LockIcon className="h-3 w-3" />
                    Locked
                  </Badge>
                )}


              </div>

              {recipe.description && (
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  {recipe.description}
                </p>
              )}

              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                <span>Created {formatDate(recipe.created_at)}</span>
                {recipe.reference_weight_kg && (
                  <span>Ref: {recipe.reference_weight_kg} kg</span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCreateVersion(recipe.id, recipe.version)}
                disabled={isCreatingVersion}
                title="Create new version"
              >
                <CopyIcon className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
